import { t } from '@lingui/macro';
import { CHIP_VARIANTS } from '@toqio/toqio-web-components';

/**
 * Status → display-label + chip-variant map (PRD §6 / O-2). FE-owned: the
 * contract returns raw lifecycle values (`UNPAID`/`PAID`/…) on the list
 * (`status`) and Volt values (`COMPLETED`/…) on the detail header
 * (`paymentStatus`); both go through this one map so a BE correction is a
 * one-line change. The label is translatable; the raw key is not.
 *
 * Unknown statuses fall back to the raw value + a `neutral` chip (PRD O-2).
 */
type ChipVariant = (typeof CHIP_VARIANTS)[keyof typeof CHIP_VARIANTS];

const VARIANTS: Record<string, ChipVariant> = {
  PAID: CHIP_VARIANTS.SUCCESS,
  COMPLETED: CHIP_VARIANTS.SUCCESS,
  REJECTED: CHIP_VARIANTS.DANGER,
  FAILED: CHIP_VARIANTS.DANGER,
  // Confirmed against the live sandbox: a CANCELLED payment renders the danger
  // (red) chip, not neutral (corrects PRD O-2's best guess).
  CANCELLED: CHIP_VARIANTS.DANGER,
  UNPAID: CHIP_VARIANTS.WARNING,
  PENDING: CHIP_VARIANTS.WARNING,
};

export const statusVariant = (raw: string): ChipVariant =>
  VARIANTS[raw?.toUpperCase()] ?? CHIP_VARIANTS.NEUTRAL;

export const statusLabel = (raw: string): string => {
  switch (raw?.toUpperCase()) {
    case 'UNPAID':
    case 'PENDING':
      return t`Pending`;
    case 'PAID':
    case 'COMPLETED':
      return t`Completed`;
    case 'FAILED':
      return t`Failed`;
    case 'REJECTED':
      return t`Rejected`;
    case 'CANCELLED':
      return t`Cancelled`;
    default:
      return raw;
  }
};

/** Raw values for the status filter (PRD §4a/§10), labelled at render time. */
export const STATUS_FILTER_OPTIONS: RawFilterOption[] = [
  { value: 'UNPAID', label: () => t`Pending` },
  { value: 'PAID', label: () => t`Completed` },
  { value: 'FAILED', label: () => t`Failed` },
  { value: 'REJECTED', label: () => t`Rejected` },
  { value: 'CANCELLED', label: () => t`Cancelled` },
];

/**
 * "Destination of funds" options (PRD O-14, Phase-2 default): the four observed
 * names, hardcoded as a flow constant and sent verbatim as `clientId` (the
 * contract resolves the text value to a client id server-side). Kept in one
 * place so swapping to a fetched list later is contained.
 */
export const DESTINATION_OPTIONS: RawFilterOption[] = [
  { value: 'HEFAME', label: () => 'HEFAME' },
  { value: 'INTERHOGAR', label: () => 'INTERHOGAR' },
  { value: 'FMAS', label: () => 'FMAS' },
  { value: 'HEFAME INFORMATICA', label: () => 'HEFAME INFORMATICA' },
];

export type RawFilterOption = { value: string; label: () => string };

/**
 * Status labels for the active-filter tags. The live renders these in English
 * even under the es locale (e.g. "Pending"), so the tag is not localized —
 * matched exactly here.
 */
export const STATUS_TAG_LABELS: Record<string, string> = {
  UNPAID: 'Pending',
  PAID: 'Completed',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};
